import {
  ApiOutlined,
  ClusterOutlined,
  CloudServerOutlined,
  DatabaseOutlined,
  DesktopOutlined,
  GoldOutlined,
  HddOutlined,
  ReloadOutlined,
  ThunderboltOutlined,
  UnorderedListOutlined,
} from '@ant-design/icons'
import { Table, Tag, Tooltip } from 'antd'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { platformGetAdminOverview } from '../api'
import type { AdminOverview, AdminWorkerRow } from '../types'
import { SectionCard, SummaryCard, UsageBar, WorkerStatusBadge, formatBytes, formatFreshness, formatRelativeTime } from './adminShared'
import {
  DEFAULT_OVERVIEW_WORKER_SORT,
  cycleOverviewNameSort,
  cycleOverviewStatusSort,
  sortOverviewWorkers,
  type OverviewWorkerSort,
} from './overviewWorkerSort'
import { cardIconBg, platformColors } from './theme'
import TaskControlSection from './TaskControlSection'

/** VistoeraTASK-00678 — clickable Overview Worker table header with ↑/↓. */
function OverviewWorkerSortHeader({
  label,
  active,
  arrow,
  onClick,
}: {
  label: string
  active: boolean
  arrow: '↑' | '↓' | null
  onClick: () => void
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        margin: 0,
        padding: 0,
        border: 'none',
        background: 'transparent',
        cursor: 'pointer',
        display: 'inline-flex',
        alignItems: 'center',
        gap: 4,
        color: active ? platformColors.primaryText : platformColors.secondaryText,
        fontWeight: active ? 600 : 500,
        fontSize: 'inherit',
        fontFamily: 'inherit',
        lineHeight: 'inherit',
      }}
      aria-label={`${label}排序`}
    >
      <span>{label}</span>
      {arrow ? <span style={{ fontSize: 11, color: platformColors.mutedText }}>{arrow}</span> : null}
    </button>
  )
}

/** VISTOERA_WORKER_CAPABILITY_ASSIGNMENT_OPERATIONAL_FIX_V1_1 item 3 — the
 * "任务调度异常" banner click-through needs a real `job_type` to filter
 * Task Control by (see `app.domain.workers.CAPABILITY_ID_FOR_JOB_TYPE` on
 * the backend, kept in sync with this one). */
const CAPABILITY_TO_JOB_TYPE: Record<string, string> = {
  'image.pipeline': 'run_chain_pipeline',
  'asset.deepzoom': 'generate_saas_deepzoom',
  video_generation: 'video_generation',
  'flat_image.optimization': 'run_flat_image_pipeline',
  // VistoeraTASK-00329 — capture channels are per platform, so a capability
  // maps to that platform's own job_type. The legacy pair stays so a
  // historical row still resolves to a filterable job_type.
  'reference.capture.cn': 'reference_content_capture',
  'reference.capture.douyin': 'reference_content_capture_douyin',
  'reference.capture.xiaohongshu': 'reference_content_capture_xiaohongshu',
  'reference.media.inspect': 'reference_content_media_analysis',
  'reference.ai.deep_analyze': 'ai_harness_chatgpt_vision',
}

/** Platform Admin Shell V0.2 spec §45 — plain 10s polling, no WebSocket/SSE. */
const AUTO_REFRESH_MS = 10_000
/** A single transient poll failure must not blank out visibly-fresh data
 * under a scary permanent-looking error banner (spec §七十三 — the
 * "Platform admin access required" banner appearing next to fresh data
 * bug). Only surface the error banner after this many *consecutive*
 * failed polls when there is already good data on screen; a lone network
 * hiccup silently retries on the next tick instead. The very first load
 * (no `data` yet) always surfaces an error immediately — there is nothing
 * stale to protect there. */
const CONSECUTIVE_FAILURES_BEFORE_BANNER = 2

export default function PlatformOverviewPage() {
  const navigate = useNavigate()
  const [data, setData] = useState<AdminOverview | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [loading, setLoading] = useState(true)
  const [refreshing, setRefreshing] = useState(false)
  const [lastRefreshedAt, setLastRefreshedAt] = useState<Date | null>(null)
  const [taskFocus, setTaskFocus] = useState<{ jobType: string; focusToken: number } | null>(null)
  // VistoeraTASK-00678 — Overview Worker 状态 table sort (frontend-only).
  // Resets to name asc on full page remount; survives 10s poll refreshes.
  const [workerSort, setWorkerSort] = useState<OverviewWorkerSort>(DEFAULT_OVERVIEW_WORKER_SORT)
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null)
  const consecutiveFailuresRef = useRef(0)
  const hasDataRef = useRef(false)
  // VistoeraTASK-00564 P0 — real-evidence finding: `/saas/platform/overview`
  // is a heavy aggregate (measured ~1-2.6s per call on Production even
  // sequentially); under even 5 concurrent calls (e.g. several admin tabs,
  // or one slow poll still in flight when the next 10s tick fires) latency
  // balloons to ~8-9s per call. Without this guard, a slow poll never
  // stopped the next `setInterval` tick from firing a brand new request on
  // top of it — exactly the "setInterval 请求重叠" pattern this task asked
  // to check for, and the single biggest amplifier turning "sometimes slow"
  // into "数据加载失败". This ref makes `load()` a no-op while a previous
  // call for this page is still in flight; the manual "刷新数据" button
  // reuses the same `load()` so it is covered too.
  const inFlightRef = useRef(false)
  // Task §五 example check — "组件卸载后仍然请求": the auto-poll interval
  // is already cleared on unmount (below), so no *new* request starts
  // after unmount; this additionally guards the case where a request was
  // already in flight at the moment of unmount and its promise settles
  // afterwards, so it never calls `setState` on an unmounted page.
  const isMountedRef = useRef(true)
  useEffect(() => {
    isMountedRef.current = true
    return () => {
      isMountedRef.current = false
    }
  }, [])
  // P0 spec item 22 — "数据加载失败" banner must show a LIVE staleness
  // age ("当前显示 38 秒前数据"), not a fixed clock timestamp that never
  // updates while the error persists. This tick forces a re-render each
  // second so `formatFreshness(lastRefreshedAt)` keeps advancing; only
  // runs while an error banner is actually visible (never during normal
  // 10s polling) to avoid extra work in the common case.
  const [, setStaleTick] = useState(0)
  useEffect(() => {
    if (!error) return
    const id = setInterval(() => setStaleTick((t) => t + 1), 1000)
    return () => clearInterval(id)
  }, [error])

  const load = useCallback((opts?: { silent?: boolean }) => {
    // Overlap guard (see `inFlightRef` docstring above). The manual
    // "刷新数据" button is disabled while `refreshing` is true, which
    // already prevents a human-triggered overlap; this additionally
    // covers the silent 10s auto-poll, which has no such UI disable.
    if (inFlightRef.current) return
    inFlightRef.current = true
    if (!opts?.silent) setRefreshing(true)
    platformGetAdminOverview()
      .then((res) => {
        if (!isMountedRef.current) return
        consecutiveFailuresRef.current = 0
        hasDataRef.current = true
        setData(res.data)
        setError(null)
        setLastRefreshedAt(new Date())
      })
      .catch((err) => {
        if (!isMountedRef.current) return
        consecutiveFailuresRef.current += 1
        // Spec §七十三 fix — never let a single transient poll failure hide
        // (or contradict) data that is already on screen. Only the very
        // first load, or `CONSECUTIVE_FAILURES_BEFORE_BANNER` failures in a
        // row, actually surface the banner; existing good data is never
        // cleared by a failed poll.
        if (!hasDataRef.current || consecutiveFailuresRef.current >= CONSECUTIVE_FAILURES_BEFORE_BANNER) {
          setError(err.message || '数据加载失败')
        }
      })
      .finally(() => {
        inFlightRef.current = false
        if (!isMountedRef.current) return
        setLoading(false)
        setRefreshing(false)
      })
  }, [])

  useEffect(() => {
    load()
    intervalRef.current = setInterval(() => {
      // VistoeraTASK-00564 P0 — page-hidden backoff (task §五 example
      // fix: "页面不可见时降低刷新频率"). A background/minimized Platform
      // Admin tab has no reason to keep contributing concurrent load to
      // the same heavy endpoint every 10s; skipping the tick here (rather
      // than lengthening the interval) means the moment the tab becomes
      // visible again the very next tick still fires on schedule, and the
      // visibilitychange listener below forces one immediate refresh so
      // the admin never sees stale data merely for having switched tabs.
      if (document.visibilityState === 'hidden') return
      load({ silent: true })
    }, AUTO_REFRESH_MS)
    const onVisible = () => {
      if (document.visibilityState === 'visible') load({ silent: true })
    }
    document.addEventListener('visibilitychange', onVisible)
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current)
      document.removeEventListener('visibilitychange', onVisible)
    }
  }, [load])

  const summary = data?.summary
  const resource = data?.resource_overview
  const storageAvailable = summary ? summary.registered_storage_bytes !== null : false
  const showSkeleton = loading && !data
  // VistoeraTASK-00678 — derive sorted rows; keep `data.workers` untouched for Task Control.
  const sortedOverviewWorkers = useMemo(
    () => sortOverviewWorkers(data?.workers ?? [], workerSort),
    [data?.workers, workerSort],
  )
  const nameSortArrow: '↑' | '↓' | null =
    workerSort.key === 'name' ? (workerSort.order === 'asc' ? '↑' : '↓') : null
  const statusSortArrow: '↑' | '↓' | null =
    workerSort.key === 'status' ? (workerSort.order === 'online_first' ? '↑' : '↓') : null

  return (
    <div style={{ maxWidth: 1600 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 10, marginBottom: 8 }}>
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <div style={{ fontSize: 20, lineHeight: '26px', fontWeight: 600, color: platformColors.primaryText }}>平台概览</div>
            {data?.environment_label && (
              <Tooltip title="环境身份来自部署配置（resource_environment / APP_ENV），不可在前端切换；Preview 与 Production 数据严格隔离">
                <span
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    fontSize: 12,
                    fontWeight: 600,
                    padding: '2px 10px',
                    borderRadius: 4,
                    background: data.environment === 'production' ? platformColors.dangerBg : platformColors.infoBg,
                    color: data.environment === 'production' ? platformColors.danger : platformColors.info,
                  }}
                >
                  环境：{data.environment_label}
                </span>
              </Tooltip>
            )}
          </div>
          <div style={{ fontSize: 12, lineHeight: '16px', color: platformColors.secondaryText, marginTop: 2 }}>
            查看当前 Vistoera {data?.environment_label ?? ''} 平台的真实运行状态
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <span style={{ fontSize: 12, color: platformColors.mutedText }}>
            自动刷新 10s{lastRefreshedAt ? ` · 上次刷新 ${lastRefreshedAt.toLocaleTimeString('zh-CN', { hour12: false })}` : ''}
          </span>
          <button
            type="button"
            onClick={() => load()}
            disabled={refreshing}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 6,
              height: 28,
              padding: '0 10px',
              borderRadius: 6,
              border: `1px solid ${platformColors.border}`,
              background: '#fff',
              color: platformColors.primaryText,
              fontSize: 12,
              cursor: refreshing ? 'default' : 'pointer',
              opacity: refreshing ? 0.6 : 1,
            }}
          >
            <ReloadOutlined spin={refreshing} /> 刷新数据
          </button>
        </div>
      </div>

      {error && (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            background: platformColors.dangerBg,
            color: platformColors.danger,
            border: `1px solid ${platformColors.dangerBg}`,
            borderRadius: 8,
            padding: '7px 14px',
            marginBottom: 10,
            fontSize: 12,
          }}
        >
          <span>
            数据加载失败：{error}
            {lastRefreshedAt &&
              ` · 当前显示 ${formatFreshness(lastRefreshedAt.toISOString())} 的数据（${lastRefreshedAt.toLocaleTimeString('zh-CN', { hour12: false })}）`}
          </span>
          <button
            type="button"
            onClick={() => load()}
            style={{ background: 'transparent', border: 'none', color: platformColors.danger, fontWeight: 600, cursor: 'pointer' }}
          >
            重试
          </button>
        </div>
      )}

      {/* VISTOERA_WORKER_CAPABILITY_ASSIGNMENT_OPERATIONAL_FIX_V1_1 item 3 —
       * "任务调度异常" banner: real queued-job counts per capability with
       * zero currently-eligible Worker (online + advertises + admin-enabled
       * + not draining/disabled), computed server-side on every poll. One
       * banner row per affected capability, clickable through to the Task
       * Control queue view filtered to that capability's job_type. */}
      {data?.no_eligible_worker_alerts?.map((alert) => (
        <div
          key={alert.capability_id}
          role="button"
          tabIndex={0}
          onClick={() =>
            setTaskFocus((prev) => ({
              jobType: CAPABILITY_TO_JOB_TYPE[alert.capability_id] ?? alert.capability_id,
              focusToken: (prev?.focusToken ?? 0) + 1,
            }))
          }
          onKeyDown={(e) => {
            if (e.key !== 'Enter' && e.key !== ' ') return
            setTaskFocus((prev) => ({
              jobType: CAPABILITY_TO_JOB_TYPE[alert.capability_id] ?? alert.capability_id,
              focusToken: (prev?.focusToken ?? 0) + 1,
            }))
          }}
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            background: platformColors.dangerBg,
            color: platformColors.danger,
            border: `1px solid ${platformColors.danger}`,
            borderRadius: 8,
            padding: '7px 14px',
            marginBottom: 10,
            fontSize: 12,
            fontWeight: 500,
            cursor: 'pointer',
          }}
        >
          <span>
            {alert.message}（{alert.reason_label}）— 点击查看队列详情
          </span>
        </div>
      ))}

      {/* PC2 Unified Worker Productionization P0 (item 四十, real-evidence
       * root cause) — this legacy global switch (not per-tenant) silently
       * stops every tenant's image.pipeline Job from ever being claimed by
       * ANY worker (in-process or PC2) while a Worker itself still shows
       * Online — exactly the "5 queued, 0 running, Worker Online" symptom
       * this round chased down. Previously invisible anywhere in Platform
       * Admin; now impossible to miss. */}
      {summary?.auto_process_paused && (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            background: platformColors.warningBg ?? '#FFFBE6',
            color: platformColors.warning,
            border: `1px solid ${platformColors.warning}`,
            borderRadius: 8,
            padding: '7px 14px',
            marginBottom: 10,
            fontSize: 12,
            fontWeight: 500,
          }}
        >
          自动处理已全局暂停（auto_process_paused）—— 所有租户的 image.pipeline（图片处理链）任务都不会被任何
          Worker 领取，即使 Worker 显示 Online。这不是 Worker 故障；如非有意为之，请通过 `/chains/auto-process/resume`
          恢复。
        </div>
      )}

      {/* Platform Admin Density & Worker Liveness P0 spec §3/§4/§5 —
       * compact "SaaS Operations Console" density (never a Marketing
       * Dashboard): row 1 = 6 core metric cards, row 2 = GPU/Worker/
       * 任务队列/已登记存储 (GPU no longer gets its own full-width card,
       * spec §5). Every technical implementation detail (raw SQL source,
       * "no Cloudflare Analytics API credentials..." etc, spec §25) now
       * lives in a small (i) tooltip via `technicalNote`, never inline on
       * the card itself. */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, fontWeight: 600, color: platformColors.primaryText, marginBottom: 4, marginTop: 0 }}>
        <span>资源概览（{data?.environment_label ?? '—'} 环境）</span>
        {resource && (
          <Tooltip
            title={`刷新策略：RAM/GPU/任务总量 ${resource.refresh_policy.ram_gpu_tasks_seconds}s · 对象存储 ${
              resource.refresh_policy.object_storage_seconds / 60
            }min · 数据库 ${resource.refresh_policy.database_seconds / 60}min（非每次轮询都重新扫描）`}
          >
            <span style={{ cursor: 'help', color: platformColors.mutedText, fontSize: 11, fontWeight: 400 }}>刷新策略 ⓘ</span>
          </Tooltip>
        )}
      </div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10 }}>
        <SummaryCard
          title="总内存 RAM"
          loading={showSkeleton}
          value={resource ? formatBytes(resource.ram.total_ram_bytes) : '—'}
          hint={
            resource?.ram.total_ram_bytes != null ? (
              <>
                <UsageBar used={resource.ram.used_ram_bytes} total={resource.ram.online_ram_bytes} />
                <span>
                  在线 {formatBytes(resource.ram.online_ram_bytes)} · 已用 {formatBytes(resource.ram.used_ram_bytes)}
                </span>
              </>
            ) : (
              '暂无 Worker 上报内存信息'
            )
          }
          technicalNote={
            resource?.ram.total_ram_bytes != null
              ? `已登记总内存：${formatBytes(resource.ram.total_ram_bytes)}（${resource.ram.workers_reporting}/${resource.ram.workers_total} Worker 上报）`
              : null
          }
          iconBg={cardIconBg.worker}
          icon={<DesktopOutlined style={{ color: '#7C3AED' }} />}
        />
        <SummaryCard
          title="对象存储（Cloudflare R2）"
          loading={showSkeleton}
          value={resource?.object_storage.available ? formatBytes(resource.object_storage.used_bytes) : '暂不可用'}
          hint={
            resource?.object_storage.available
              ? `对象数 ${resource.object_storage.object_count?.toLocaleString('zh-CN') ?? '—'} · 无固定套餐总容量（usage-based）— 点击查看用量分布/Top Consumers/清理审计`
              : '暂未配置或扫描失败'
          }
          technicalNote={!resource?.object_storage.available ? resource?.object_storage.reason ?? null : null}
          iconBg={cardIconBg.storage}
          icon={<CloudServerOutlined style={{ color: platformColors.info }} />}
          onClick={() => navigate('/platform/storage')}
        />
        <SummaryCard
          title="Worker 临时存储"
          loading={showSkeleton}
          value={resource?.worker_storage.available ? formatBytes(resource.worker_storage.used_bytes) : '暂不可用'}
          hint={resource?.worker_storage.reason ? '暂无磁盘遥测' : undefined}
          technicalNote={resource?.worker_storage.reason ?? null}
          iconBg={cardIconBg.storage}
          icon={<HddOutlined style={{ color: platformColors.secondaryText }} />}
        />
        <SummaryCard
          title={
            resource?.database.source?.startsWith('postgresql:')
              ? '数据库（Render PostgreSQL）'
              : resource?.database.source?.startsWith('sqlite:')
                ? '数据库（本地 SQLite）'
                : '数据库存储'
          }
          loading={showSkeleton}
          value={resource?.database.available ? formatBytes(resource.database.bytes) : '暂不可用'}
          hint={
            resource?.database.available
              ? `数据库统计正常 — 点击查看 Render Backend/Database 详情（Region/Plan/Deploy）`
              : '暂不可用'
          }
          technicalNote={resource?.database.available ? resource?.database.source ?? null : resource?.database.reason ?? null}
          iconBg={cardIconBg.storage}
          icon={<DatabaseOutlined style={{ color: platformColors.secondaryText }} />}
          onClick={() => navigate('/platform/storage?tab=render')}
        />
        <SummaryCard
          title="带宽使用（本月）"
          loading={showSkeleton}
          value={resource?.bandwidth.available ? formatBytes(resource.bandwidth.used_bytes) : '暂未配置'}
          hint={resource?.bandwidth.available ? undefined : '暂未配置统计来源'}
          technicalNote={resource?.bandwidth.reason ?? null}
          iconBg={cardIconBg.task}
          icon={<ThunderboltOutlined style={{ color: platformColors.warning }} />}
        />
        <SummaryCard
          title="任务总量（今日）"
          loading={showSkeleton}
          value={resource ? resource.tasks_today.total : '—'}
          hint={resource ? `成功 ${resource.tasks_today.succeeded} · 失败 ${resource.tasks_today.failed} · 运行中 ${resource.tasks_today.running} · 排队 ${resource.tasks_today.queued}` : undefined}
          iconBg={cardIconBg.task}
          icon={<UnorderedListOutlined style={{ color: platformColors.warning }} />}
        />
      </div>

      {/* Spec §5 — GPU folded into the second compact row alongside
       * Worker/任务队列/已登记存储, never a lone full-width card again. */}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 8 }}>
        {resource && resource.gpu.gpu_total > 0 && (
          <SummaryCard
            title="GPU 资源"
            loading={showSkeleton}
            value={`${resource.gpu.gpu_online} / ${resource.gpu.gpu_total} 在线`}
            hint={`VRAM ${formatBytes(resource.gpu.vram_used_bytes)} / ${formatBytes(resource.gpu.vram_total_bytes)}`}
            iconBg={cardIconBg.worker}
            icon={<GoldOutlined style={{ color: '#7C3AED' }} />}
          />
        )}
        <SummaryCard
          title="Worker"
          loading={showSkeleton}
          value={summary ? `${summary.workers_online} / ${summary.workers_total}` : '—'}
          hint="在线 / 总数"
          iconBg={cardIconBg.worker}
          icon={<ClusterOutlined style={{ color: '#7C3AED' }} />}
        />
        <SummaryCard
          title="任务队列"
          loading={showSkeleton}
          value={summary ? `${summary.tasks_queued} / ${summary.tasks_running} / ${summary.tasks_failed}` : '—'}
          hint="排队 / 运行中 / 失败"
          iconBg={cardIconBg.task}
          icon={<UnorderedListOutlined style={{ color: platformColors.warning }} />}
        />
        {/* External Services / Infrastructure Registry (P0-B spec §21) —
         * "7 services / 6 healthy / 1 not configured / 0 expiring" style
         * summary, click-through into the dedicated "外部服务" page rather
         * than listing every provider inline here. */}
        <SummaryCard
          title="外部服务"
          loading={showSkeleton}
          value={data ? `${data.external_services_summary.healthy} / ${data.external_services_summary.total}` : '—'}
          hint={
            data
              ? `Healthy ${data.external_services_summary.healthy} · 未配置 ${data.external_services_summary.not_configured} · 即将到期 ${data.external_services_summary.expiring}`
              : undefined
          }
          iconBg={cardIconBg.domain}
          icon={<ApiOutlined style={{ color: platformColors.info }} />}
          onClick={() => navigate('/platform/external-services')}
        />
        <SummaryCard
          title="已登记存储"
          loading={showSkeleton}
          value={storageAvailable ? formatBytes(summary!.registered_storage_bytes as number) : '暂不可用'}
          hint={storageAvailable ? '原图 + 变体文件' : '尚未接入可靠统计口径'}
          iconBg={cardIconBg.storage}
          icon={<DatabaseOutlined style={{ color: platformColors.secondaryText }} />}
        />
      </div>

      <SectionCard title="Worker 状态">
        <Table<AdminWorkerRow>
          className="pf-data-table"
          rowKey="worker_id"
          loading={showSkeleton}
          dataSource={sortedOverviewWorkers}
          pagination={false}
          scroll={{ x: true }}
          sticky
          size="small"
          locale={{ emptyText: '暂无 Worker 数据' }}
          columns={[
            {
              // VistoeraTASK-00678 — default natural name asc; click toggles asc/desc.
              title: (
                <OverviewWorkerSortHeader
                  label="Worker"
                  active={workerSort.key === 'name'}
                  arrow={nameSortArrow}
                  onClick={() => setWorkerSort((prev) => cycleOverviewNameSort(prev))}
                />
              ),
              dataIndex: 'name',
              align: 'left' as const,
              render: (v: string | null, row: AdminWorkerRow) => (
                <div style={{ lineHeight: 1.25 }}>
                  <div style={{ fontWeight: 600 }}>{v || row.worker_id}</div>
                  <div style={{ color: platformColors.mutedText, fontSize: 11, fontFamily: 'monospace' }}>{row.worker_id}</div>
                </div>
              ),
            },
            {
              // VistoeraTASK-00678 — click toggles online-first / offline-first;
              // within a bucket, natural name asc. Unknown display_status ≠ online.
              title: (
                <OverviewWorkerSortHeader
                  label="状态"
                  active={workerSort.key === 'status'}
                  arrow={statusSortArrow}
                  onClick={() => setWorkerSort((prev) => cycleOverviewStatusSort(prev))}
                />
              ),
              key: 'status',
              // Platform Admin Density & Worker Liveness P0 spec §14/§15 —
              // renders the backend's single canonical `display_status`
              // (Online/Busy/Offline/Draining/Disabled), never recomputes
              // it from `online` alone anymore.
              render: (_: unknown, row: AdminWorkerRow) => <WorkerStatusBadge displayStatus={row.display_status} online={row.online} />,
            },
            {
              title: '能力',
              dataIndex: 'capabilities',
              align: 'left' as const,
              render: (caps: string[]) =>
                caps.length ? caps.map((c) => <Tag key={c} style={{ fontSize: 11, padding: '0 5px', lineHeight: '16px', margin: '1px' }}>{c}</Tag>) : '—',
            },
            {
              title: 'RAM',
              key: 'ram',
              render: (_: unknown, row: AdminWorkerRow) => {
                const totalRam = (row.resource_profile?.total_ram_bytes as number | undefined) ?? null
                return totalRam ? formatBytes(totalRam) : '—'
              },
            },
            {
              title: 'GPU VRAM',
              key: 'vram',
              render: (_: unknown, row: AdminWorkerRow) => {
                const snap = row.resource_snapshot
                if (!snap || snap.gpu_total_vram_mb == null) return '—'
                const usedMb = snap.gpu_free_vram_mb != null ? snap.gpu_total_vram_mb - snap.gpu_free_vram_mb : null
                return `${usedMb != null ? (usedMb / 1024).toFixed(1) : '—'} / ${(snap.gpu_total_vram_mb / 1024).toFixed(1)} GB`
              },
            },
            {
              title: '当前任务',
              dataIndex: 'current_task',
              align: 'left' as const,
              render: (task: AdminWorkerRow['current_task']) => (task ? `${task.job_type_label} #${task.job_id.slice(0, 8)}` : '—'),
            },
            {
              title: '并发',
              key: 'concurrency',
              render: (_: unknown, row: AdminWorkerRow) => (row.concurrency_max !== null ? `${row.concurrency_used ?? 0} / ${row.concurrency_max}` : '—'),
            },
            {
              title: '最后心跳',
              key: 'last_seen_at',
              render: (_: unknown, row: AdminWorkerRow) => (
                <Tooltip title={row.heartbeat_age_seconds != null ? `${Math.round(row.heartbeat_age_seconds)} 秒前` : undefined}>
                  <span>{formatRelativeTime(row.last_seen_at)}</span>
                </Tooltip>
              ),
            },
          ]}
        />
      </SectionCard>

      {/* Platform Admin Resource & Task Control V2 spec §十六-§七十二/§八十九-
       * §九十二 — 平台概览的"最近任务"升级为真正的"任务控制中心"：筛选/搜索/
       * 分页/详情 Drawer/单条+批量删除/手工调整队列顺序。 */}
      <TaskControlSection workers={data?.workers ?? []} focusRequest={taskFocus} />
    </div>
  )
}
